<template>
    <div v-if="visible" class="hard-copy-prescriptions mt-2 mb-2">
        <div v-if="samePatientPrescriptions.length > 1">
            <div class="hard-copy-header d-flex align-items-center" @click="expanded = !expanded" style="cursor: pointer;">
                <i :class="['fa', expanded ? 'fa-chevron-down' : 'fa-chevron-right', 'mr-1']"></i>
                <strong>{{ samePatientPrescriptions.length }} prescription{{ samePatientPrescriptions.length !== 1 ? 's' : '' }} on this hard copy</strong>
            </div>
            <div v-if="expanded" class="prescription-list mt-1">
                <div v-for="rx in samePatientPrescriptions" :key="`${rx.storeID}-${rx.rxNumber}-${rx.rfNumber}`"
                     class="rx-item p-1 pl-2 pr-2 d-flex align-items-center"
                     :class="{ 'current-rx': isCurrent(rx) }">
                    <template v-if="isCurrent(rx)">
                        <span class="rx-id font-weight-bold">{{ rx.storeID }}-{{ rx.rxNumber }}-{{ rx.rfNumber }}</span>
                        <span class="ml-2 text-muted">{{ rx.patientLastName }}, {{ rx.patientFirstName }}</span>
                        <span v-if="rx.drugName" class="ml-2 text-muted">- {{ rx.drugName }}</span>
                        <span class="badge badge-primary ml-auto">Current</span>
                    </template>
                    <template v-else>
                        <router-link :to="linkTo(rx)" target="_blank">
                            {{ rx.storeID }}-{{ rx.rxNumber }}-{{ rx.rfNumber }}
                        </router-link>
                        <span class="ml-2">{{ rx.patientLastName }}, {{ rx.patientFirstName }}</span>
                        <span v-if="rx.drugName" class="ml-2 text-muted">- {{ rx.drugName }}</span>
                    </template>
                </div>
            </div>
        </div>

        <div v-if="crossPatientPrescriptions.length > 0" class="cross-patient-note mt-1 p-1 pl-2 pr-2">
            <i class="fa fa-info-circle mr-1"></i>
            <span>
                {{ crossPatientPrescriptions.length }} other prescription{{ crossPatientPrescriptions.length !== 1 ? 's' : '' }}
                saved from this image belong{{ crossPatientPrescriptions.length === 1 ? 's' : '' }} to a different patient.
            </span>
            <a v-if="showCrossPatient" href="#" class="ml-1" @click.prevent="crossExpanded = !crossExpanded">
                {{ crossExpanded ? 'Hide' : 'Show' }}
            </a>
        </div>
        <div v-if="showCrossPatient && crossExpanded" class="prescription-list mt-1">
            <div v-for="rx in crossPatientPrescriptions" :key="`${rx.storeID}-${rx.rxNumber}-${rx.rfNumber}`"
                 class="rx-item p-1 pl-2 pr-2">
                <router-link :to="linkTo(rx)" target="_blank">
                    {{ rx.storeID }}-{{ rx.rxNumber }}-{{ rx.rfNumber }}
                </router-link>
                <span class="ml-2">{{ rx.patientLastName }}, {{ rx.patientFirstName }}</span>
                <span v-if="rx.drugName" class="ml-2 text-muted">- {{ rx.drugName }}</span>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { Component, Emit, Prop, Vue, Watch } from 'vue-property-decorator';
import Axios from 'axios';
import { PrescriptionOnHardCopy } from '@/models/PrescriptionOnHardCopy';

@Component({ name: 'HardCopyPrescriptionsComponent' })
export default class HardCopyPrescriptionsComponent extends Vue {
    @Prop() imageID!: number | null;
    @Prop() currentStoreID!: number;
    @Prop() currentRxNumber!: number;
    @Prop() currentRfNumber!: number;
    @Prop() currentPatientID!: number;
    @Prop({ default: false }) showCrossPatient!: boolean;

    private prescriptions: PrescriptionOnHardCopy[] = [];
    private expanded = true;
    private crossExpanded = false;

    get samePatientPrescriptions(): PrescriptionOnHardCopy[] {
        // Without a known patient (e.g. typing a new script from a chopped image),
        // we can't say which siblings are cross-patient, so classify none.
        if (!this.currentPatientID) return [];
        return this.prescriptions.filter(rx => rx.patientID === this.currentPatientID);
    }

    get crossPatientPrescriptions(): PrescriptionOnHardCopy[] {
        if (!this.currentPatientID) return [];
        return this.prescriptions.filter(rx => rx.patientID !== this.currentPatientID);
    }

    get visible(): boolean {
        return this.samePatientPrescriptions.length > 1 || this.crossPatientPrescriptions.length > 0;
    }

    @Emit('count-changed')
    emitCountChanged(count: number) {
        return count;
    }

    @Watch('imageID', { immediate: true })
    async onImageIdChanged(newVal: number | null): Promise<void> {
        this.prescriptions = [];
        this.crossExpanded = false;
        if (!newVal) {
            this.emitCountChanged(0);
            return;
        }
        try {
            const response = await Axios.get<PrescriptionOnHardCopy[]>(`/Prescription/by-image/${newVal}`);
            this.prescriptions = response.data;
            this.expanded = this.samePatientPrescriptions.length > 1;
        } catch {
            this.prescriptions = [];
        }
        // Emit the full count: image annotations propagate to every prescription sharing
        // the source image, regardless of patient, so the sibling count must include them.
        this.emitCountChanged(this.prescriptions.length);
    }

    isCurrent(rx: PrescriptionOnHardCopy): boolean {
        return rx.storeID === this.currentStoreID
            && rx.rxNumber === this.currentRxNumber
            && rx.rfNumber === this.currentRfNumber;
    }

    linkTo(rx: PrescriptionOnHardCopy): object {
        return {
            name: 'PrescriptionDetailsId',
            params: { rxId: `${rx.storeID}-${rx.rxNumber}-${rx.rfNumber}` },
        };
    }
}
</script>

<style scoped>
.hard-copy-prescriptions {
    border: 1px solid #dee2e6;
    border-radius: 4px;
    padding: 6px 10px;
    background: #f8f9fa;
    font-size: 0.875rem;
}

.rx-item {
    border-radius: 3px;
}

.rx-item.current-rx {
    border-left: 3px solid #007bff;
    background: #e7f1ff;
}

.cross-patient-note {
    color: #856404;
    background: #fff3cd;
    border: 1px solid #ffeeba;
    border-radius: 3px;
}
</style>
